import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { listings, listingSales } from '../../actions'
import ConfigContext from '../../contexts/configContext'
import trackingEvents from '../../constants/trackingEvents'
import PURCHASE_TYPES from '../../constants/purchaseTypes'
import { translate } from '../../helpers/stringHelpers'
import routeHelpers from '../../helpers/routeHelpers'
import { formatCurrency } from '../../helpers/currencyHelpers'
import { trackEvent, trackModal } from '../../withTracking'

// components
import Badge from '../badge'
import Button from '../button'
import CardBody from '../cardBody'
import BidButton from '../bidButton'
import AutoBidButton from '../autoBidButton'
import PurchaseButton from '../purchaseButton'
import ListingCountdown from '../listingCountdown'
import EndTimeAwareButton from '../endTimeAwareButton'
import PurchaseConfirmationModalAutoBid from '../purchaseConfirmationModalAutoBid'
import PurchaseConfirmationModalBid from '../purchaseConfirmationModalBid'
import PurchaseConfirmationModalBuy from '../purchaseConfirmationModalBuy'
import PurchaseConfirmationModalOffer from '../purchaseConfirmationModalOffer'
import PurchaseConfirmationModalTender from '../purchaseConfirmationModalTender'
import BidHistoryModal from '../bidHistoryModal'

const {
  CATEGORY_LISTING,
  ACTION_BID,
  MODAL_AUTOBID_CONFIRM,
  MODAL_BID_CONFIRM,
  MODAL_BUY_CONFIRM,
  MODAL_OFFER_CONFIRM,
  MODAL_TENDER_CONFIRM
} = trackingEvents

const PURCHASED_ENDED_MESSAGES = {
  2: 'ItemMessageStatus_LISTING_CLOSED_MESSAGE_SOLD_MAKE_OFFER',
  3: 'ItemMessageStatus_LISTING_CLOSED_MESSAGE_SOLD_BUY_NOW',
  5: 'ItemMessageStatus_LISTING_CLOSED_MESSAGE_SOLD_TENDER',
  6: 'ItemMessageStatus_LISTING_CLOSED_MESSAGE_SOLD_TENDER_PCT'
}

const generateBidStatus = (contentCode) => (
  <small className='lot__info--bid-status'>
    {translate(contentCode)}
  </small>
)

const getWinningMessage = (purchaseTypeId, isMarketplace = false) => {
  switch (purchaseTypeId) {
    case PURCHASE_TYPES.BUY_NOW:
      return 'JspPublicCodes_JSP_LISTING_BUY_SUCCESSFUL'

    case PURCHASE_TYPES.MAKE_OFFER:
    case PURCHASE_TYPES.TENDER:
    case PURCHASE_TYPES.TENDER_PCT:
      return isMarketplace
        ? 'JspPublicCodes_JSP_MARKETPLACE_OFFER_APPROVED'
        : 'JspPublicCodes_JSP_LISTING_OFFER_APPROVED'

    default:
      return ''
  }
}

export const ListingSale = (props) => {
  const { hasStarted, listingUuid } = props
  const { locale } = useContext(ConfigContext)

  // redux state
  const dispatch = useDispatch()
  const listing = useSelector(state => state.lots.listings[listingUuid], shallowEqual)
  const isMarketplace = listing?.isMarketplace
  const auctionUuid = listing?.auctionUuid
  const marketplaceFriendlyAuctionUuid = isMarketplace ? 'marketplace' : auctionUuid
  const auctionStart = useSelector(state => state.auctions.auctions[marketplaceFriendlyAuctionUuid]?.startsAt || 0)
  const auctionStatus = useSelector(state => state.auctions.auctions[marketplaceFriendlyAuctionUuid]?.status)
  const isAuctionEnded = auctionStatus === 'RECONCILIATION' || auctionStatus === 'ARCHIVED'
  const isWebcast = useSelector(state => state.auctions.auctions[marketplaceFriendlyAuctionUuid]?.webcast)
  const configuration = useSelector(state => state.auctions.configurations[marketplaceFriendlyAuctionUuid] || {}, shallowEqual)
  const sale = useSelector(state => state.listingSales.sales[listingUuid], shallowEqual)
  const bids = useSelector(state => sale?.bids?.reduce((acc, val) => {
    acc[val] = state.bids.bids[val]
    return acc
  }, {}), shallowEqual)
  const saleStatus = useSelector(state => state.saleStatuses.saleStatuses[listingUuid], shallowEqual)
  const autoBidValue = useSelector(state => state.maxBids.maxBidsByListingUuid[listingUuid]?.value)
  const offers = useSelector(state => state.offers.offersByListing[listingUuid]
    ?.map(offerUuid => state.offers.offers[offerUuid]), shallowEqual)
  const pendingOffers = offers && offers.filter(offer => !offer || !offer.rejected)
  const userId = useSelector(state => state.users.user.id)
  const userUuid = useSelector(state => state.users.user.uuid)
  const registrations = useSelector(state => state.registrations.registrantIdsByUserUuidByAuction[auctionUuid], shallowEqual)
  const registration = userUuid && registrations?.[userUuid]
  const isBiddingRequiredForReserve = useSelector(state => !!state.config.input.options.biddingRequiredForReserve)
  const isBidHistoryDisabled = useSelector(state => !!state.config.input.options.disableBidHistory)
  const isConsecutiveBiddingDisabled = useSelector(state => state.config.input.options.disableConsecutiveBidding)
  const isFavourited = useSelector(state => state.lots.favourites[marketplaceFriendlyAuctionUuid]?.[listingUuid])
  const categoryId = useSelector(state => state.categories.categoryChildren[listing?.categoryUuid]?.id)
  const catalogueUrl = useSelector(state => state.auctions.information[marketplaceFriendlyAuctionUuid]?.catalogueUrl)
  const preBidding = useSelector(state => state.auctions.information[marketplaceFriendlyAuctionUuid]?.preBidding)
  const { login: isLoginModuleEnabled, register: isRegisterModuleEnabled } = useSelector(state => state.config.input.modules, shallowEqual)

  // component state
  const [isAwaitingBidResponse, setAwaitingBidResponse] = useState(false)
  const [isBidConfirmationVisible, setBidConfirmationVisibility] = useState(false)
  const [isBidConfirmationOptOut, setBidConfirmationOptOut] = useState(false)
  const [isBidHistoryVisible, setBidHistoryVisibility] = useState(false)
  const [isAutobidConfirmationVisible, setAutobidConfirmationVisibility] = useState(false)
  const [isBuyConfirmationVisible, setBuyConfirmationVisibility] = useState(false)
  const [isOfferConfirmationVisible, setOfferConfirmationVisibility] = useState(false)
  const [isTenderConfirmationVisible, setTenderConfirmationVisibility] = useState(false)

  const getListingPurchaseMethod = (purchaseTypeId) => {
    switch (purchaseTypeId) {
      case PURCHASE_TYPES.MAKE_OFFER:
        return (isVisible) => {
          trackModal(MODAL_OFFER_CONFIRM)
          setOfferConfirmationVisibility(isVisible)
        }
      case PURCHASE_TYPES.BUY_NOW:
        return (isVisible) => {
          trackModal(MODAL_BUY_CONFIRM)
          setBuyConfirmationVisibility(isVisible)
        }
      case PURCHASE_TYPES.TENDER:
      case PURCHASE_TYPES.TENDER_PCT:
        return (isVisible) => {
          trackModal(MODAL_TENDER_CONFIRM)
          setTenderConfirmationVisibility(isVisible)
        }
      case PURCHASE_TYPES.NONE:
      default:
        return () => { /* no purchase method is required */ }
    }
  }

  if (!listing || !sale) return null

  const currencyFormat = formatCurrency.bind(null, locale, sale.currencyCode)
  const currentTimestamp = Date.now()

  const isLot = listing.type === 'lot'
  const isListing = listing.type === 'listing'
  const bidCount = sale.bids.length || 0
  const isPurchased = saleStatus.sold && !!saleStatus.highestOfferUuid
  const bidCountContent = bidCount && `${bidCount} ${translate(bidCount === 1 ? 'JspPublicCodes_JSP_BID_SINGLE' : 'JspPublicCodes_JSP_BID_MULTIPLE')}`

  const userHasBid = !!sale.bids && !!sale.bids.filter(bidUuid => bids[bidUuid]?.userUuid === userUuid).length
  const highestBid = !!saleStatus.highestBidUuid && bids[saleStatus.highestBidUuid]
  const winningOffer = !!saleStatus.highestOfferUuid && pendingOffers && pendingOffers.find(offer => offer.uuid === saleStatus.highestOfferUuid && offer.userUuid === userUuid)
  const highestBidder = userHasBid && highestBid?.userUuid === userUuid && !saleStatus.highestOfferUuid
  const isHighestBidder = highestBidder || !!winningOffer

  const isCurrentBidVisible = configuration.showWinningBid || (!isAuctionEnded && !saleStatus.complete) || isHighestBidder
  const isReserveVisible = isCurrentBidVisible && (!isBiddingRequiredForReserve || userHasBid)
  const isPurchaseable = (!configuration.buyNowCutoff || !isLot || (currentTimestamp < saleStatus.endsAt - (configuration.buyNowCutoff * 86400000)))
  const isPreBidding = isWebcast && preBidding

  const currentBidValue = isCurrentBidVisible
    ? highestBid
      ? highestBid.amount
      : sale.nextBidValue
    : null

  const itemStatusMessage = saleStatus.sold && winningOffer
    ? translate(getWinningMessage(sale.purchaseTypeId, isMarketplace))
      .replace('{0}', formatCurrency(locale, sale.currencyCode, winningOffer.amount))
      .replace('{1}', isMarketplace ? listing.id : listing.lotNumber)
    : null

  const pendingOfferCount = (!saleStatus.complete && pendingOffers?.length) || 0

  const relatedItemsUrl = !configuration.hideRelatedItems && listing.categoryUuid && categoryId
    ? `/search?subCategory=${categoryId}`
    : null

  const disableActions = (!hasStarted || (registration && !registration.approved))

  const getRegistrationUrl = () => {
    if (registration) return null
    let url = isRegisterModuleEnabled === true
      ? `/auctions/${auctionUuid}/register`
      : isRegisterModuleEnabled
        ? isRegisterModuleEnabled.replace(/%AUCTION_ID%/gi, auctionUuid)
        : null
    if (isMarketplace) {
      url += '?redirect=/marketplace'
    }
    return url
  }

  const registerUrl = getRegistrationUrl()

  const loginUrl = userUuid
    ? null
    : isLoginModuleEnabled === true
      ? `/login?redirect=${encodeURIComponent(registerUrl)}`
      : isLoginModuleEnabled

  const protectedAction = (loginUrl || registerUrl)
    ? () => routeHelpers.navigate(loginUrl || registerUrl)
    : false

  const hasBidButton = (isPreBidding || (isLot && !configuration.showAutoBidOnly)) && !isAuctionEnded

  const handleBid = hasBidButton
    ? (bidValue, bidCalculation) => {
        if (isBidConfirmationOptOut) {
          handleBidConfirm()
        } else {
          setBidConfirmationVisibility({
            bidCalculation,
            bidValue
          })
          trackModal(MODAL_BID_CONFIRM)
        }
      }
    : null

  const handleBidConfirm = async (e) => {
    if (e) {
      e.preventDefault()
    }
    // If bidding via the modal, the amount the bid would be for is saved in state
    // This is because we don't want the value to update whilst the user has the modal open
    // If opted out of the modal, it'll be whatever the current nextBidValue is
    const bidValue = (isBidConfirmationVisible.bidValue || sale.nextBidValue)
    // Webcast is submitted in "pence" rather than full currency
    const submissionAmount = isWebcast ? bidValue * 100 : bidValue

    setAwaitingBidResponse(true)
    try {
      await dispatch(listingSales.placeTimedBid(listing.id, registration.id, userId, submissionAmount))
      trackEvent(CATEGORY_LISTING, ACTION_BID, listingUuid, bidValue)
      const confirmationMessage = isMarketplace ? 'JspPublicCodes_JSP_MARKETPLACE_BID_SUCCESSFUL' : 'JspPublicCodes_JSP_LISTING_BID_SUCCESSFUL'
      toast.success(translate(confirmationMessage)
        .replace('{0}', formatCurrency(locale, sale.currencyCode, bidValue))
        .replace('{1}', isMarketplace ? listing.id : listing.lotNumber))
      if (!isFavourited) {
        dispatch(listings.favouriteToggle(marketplaceFriendlyAuctionUuid, listingUuid, listing.id, userId))
      }
    } catch (err) {
      console.error(err)
      toast.error(err)
    }
    setBidConfirmationVisibility(false)
    setAwaitingBidResponse(false)
  }

  return (
    <div className='lot__sale'>
      <CardBody className='bg-muted lot__status' key='lot__status'>
        <div className='text-muted text-right lot__timings'>
          {saleStatus.withdrawn
            ? translate('ItemMessageStatus_ITEM_WITHDRAWN')
            : saleStatus.suspended
              ? translate('ItemMessageStatus_ITEM_SUSPENDED')
              : saleStatus.complete
                ? isPurchased
                  ? translate(PURCHASED_ENDED_MESSAGES[sale.purchaseTypeId])
                  : translate('JspPublicCodes_JSP_SALE_ENDED')
                : isAuctionEnded
                  ? translate('JspPublicCodes_JSP_SALE_ENDED')
                  : (
                  <ListingCountdown
                    auctionStartsAt={auctionStart}
                    endsAt={saleStatus.endsAt}
                    hasStarted={hasStarted}
                    isInOvertime={saleStatus.inOvertimeBidding}
                    type={listing.type}
                  />
                    )}
        </div>
        {!catalogueUrl
          ? (
            <div className='text-right'>
              <div className='lot__badges'>
                {isLot && isReserveVisible
                  ? (
                    <Badge
                      color={sale.reserve
                        ? bidCount > 0
                          ? currentBidValue >= sale.reserve
                            ? 'info'
                            : 'warning'
                          : 'warning'
                        : 'secondary'}
                      className='lot__reserve-label'
                    >
                      {translate(
                        sale.reserve
                          ? bidCount > 0
                            ? currentBidValue >= sale.reserve
                              ? 'JspPublicCodes_JSP_LISTING_RESERVE_MET'
                              : 'JspPublicCodes_JSP_LISTING_RESERVE_NOT_MET'
                            : 'JspPublicCodes_JSP_LISTING_RESERVE_NOT_MET'
                          : 'JspPublicCodes_JSP_LISTING_RESERVE_NONE'
                      )}
                    </Badge>
                    )
                  : null}
                {isPurchaseable && pendingOfferCount
                  ? (
                    <Badge
                      color='warning'
                      onClick={() => getListingPurchaseMethod(sale.purchaseTypeId)(true)}
                      className='clickable lot__offer-count'
                    >
                      {translate((pendingOfferCount > 1)
                        ? 'JspPublicCodes_JSP_LOGGED_IN_BANNER_MY_SALES_PENDING_OFFERS'
                        : 'JspPublicCodes_JSP_LOGGED_IN_BANNER_MY_SALES_PENDING_OFFER'
                      ).replace('{0}', pendingOfferCount)}
                    </Badge>
                    )
                  : null}
                {itemStatusMessage || userHasBid || autoBidValue
                  ? (
                    <Badge
                      className='lot__bid-status'
                      color={isHighestBidder
                        ? 'success'
                        : saleStatus.sold || isAuctionEnded
                          ? 'danger'
                          : 'warning'}
                    >
                      {itemStatusMessage || translate(isHighestBidder
                        ? saleStatus.sold ? 'JspPublicCodes_JSP_SALE_WON' : 'JspPublicCodes_JSP_SALE_WINNING'
                        : saleStatus.sold ? 'JspPublicCodes_JSP_SALE_LOST' : 'JspPublicCodes_JSP_SALE_LOSING'
                      )}
                    </Badge>
                    )
                  : null}
              </div>
              {currentBidValue && !isPurchased
                ? (
                  <div className='bid-content'>
                    <div>
                      {sale.numberOfUnits && sale.numberOfUnits !== 1
                        ? (
                          <div className='text-muted lot__info--quantity'>
                            {translate('JspPublicCodes_JSP_LISTING_QUANTITY').replace('{0}', sale.numberOfUnits)}
                          </div>
                          )
                        : null}
                      {bidCount
                        ? configuration.showNumberOfBids
                          ? !isBidHistoryDisabled && !isMarketplace
                              ? (
                                  <Button
                                    className='lot__info--bid-count lot__info--bid-count-btn'
                                    color='link'
                                    onClick={() => setBidHistoryVisibility(true)}
                                    size='sm'
                                  >
                                    {bidCountContent}
                                  </Button>
                                )
                              : (
                                <span className='lot__info--bid-count lot__info--bid-count-text'>
                                  {bidCountContent}
                                </span>
                                )
                          : generateBidStatus('JspPublicCodes_JSP_BIDDING_HIGHEST_BID')
                        : isListing
                          ? generateBidStatus('JspPublicCodes_JSP_BIDDING_DISPLAY_PRICE')
                          : generateBidStatus('WebcastCodes_WC_OPENING_BID_TEXT')}
                      <span className='h4 lot__info--current-bid'>
                        {currencyFormat(currentBidValue)}
                      </span>
                    </div>
                    {configuration.showBidIncrements && sale.incrementValue
                      ? (
                        <small className='text-muted lot__info--increment'>
                          {translate('JspPublicCodes_JSP_BID_INCREMENT')} {currencyFormat(sale.incrementValue)}
                        </small>
                        )
                      : null}
                  </div>
                  )
                : null}
            </div>
            )
          : null}
      </CardBody>
      {saleStatus.preview
        ? null
        : catalogueUrl
          ? (
            <CardBody className='lot__actions bg-muted' key='lot__actions'>
              <Button className='lot__action lot__action--catalogue' block href={catalogueUrl} target='_blank' rel='noopener noreferrer'>
                {translate('JspPublicCodes_JSP_CATALOGUE_GOTO_EXTERNAL_WEBSITE')}
              </Button>
            </CardBody>
            )
          : saleStatus.complete || isAuctionEnded
            ? relatedItemsUrl
              ? (
                <CardBody className='lot__actions bg-muted' key='lot__actions'>
                  <Button
                    className='lot__action lot__action--related'
                    to={relatedItemsUrl}
                    color='secondary'
                    block
                  >
                    {translate('JspPublicCodes_JSP_LISTING_BROWSE_RELATED')}
                  </Button>
                </CardBody>
                )
              : null
            : (
              <CardBody className='lot__actions bg-muted' key='lot__actions'>
                {isPurchaseable && sale.purchaseTypeId !== PURCHASE_TYPES.NONE
                  ? (
                    <EndTimeAwareButton
                      buyNowValue={isPurchaseable ? sale.buyNowValue : null}
                      currencyCode={sale.currencyCode}
                      disabled={!!disableActions}
                      element={PurchaseButton}
                      endsAt={saleStatus.endsAt}
                      isLot={isLot}
                      nextBidValue={sale.nextBidValue}
                      onPurchase={protectedAction || (() => getListingPurchaseMethod(sale.purchaseTypeId)(true))}
                      purchaseTypeId={sale.purchaseTypeId}
                      type='purchase-button'
                      block
                    />
                    )
                  : null}
                {(isPreBidding || isLot) && !isAuctionEnded
                  ? (
                    <EndTimeAwareButton
                      onClick={protectedAction || (() => {
                        trackModal(MODAL_AUTOBID_CONFIRM)
                        setAutobidConfirmationVisibility(true)
                      })}
                      currencyCode={sale.currencyCode}
                      currentValue={autoBidValue}
                      disabled={!!disableActions}
                      element={AutoBidButton}
                      endsAt={saleStatus.endsAt}
                      isPrimary={!hasBidButton}
                      type='autobid-button'
                      block
                    />
                    )
                  : null}
                {hasBidButton
                  ? (
                    <EndTimeAwareButton
                      disabled={!!(disableActions || (isConsecutiveBiddingDisabled && isHighestBidder) || isAwaitingBidResponse)}
                      element={BidButton}
                      endsAt={saleStatus.endsAt}
                      onClick={protectedAction || (() => handleBid(sale.nextBidValue, sale.nextBidCalculation))}
                      currencyCode={sale.currencyCode}
                      nextBidValue={sale.nextBidValue}
                      nextBidCalculation={sale.nextBidCalculation}
                      type='bid-button'
                      block
                    />
                    )
                  : null}
              </CardBody>
              )}
        <PurchaseConfirmationModalAutoBid
          isOpen={!!isAutobidConfirmationVisible}
          listingUuid={listingUuid}
          onClose={() => setAutobidConfirmationVisibility(false)}
        />
        <PurchaseConfirmationModalOffer
          isOpen={isOfferConfirmationVisible}
          listingUuid={listingUuid}
          onClose={() => setOfferConfirmationVisibility(false)}
        />
        <PurchaseConfirmationModalTender
          isOpen={isTenderConfirmationVisible}
          listingUuid={listingUuid}
          onClose={() => setTenderConfirmationVisibility(false)}
        />
        <PurchaseConfirmationModalBuy
          isOpen={isBuyConfirmationVisible}
          listingUuid={listingUuid}
          onClose={() => setBuyConfirmationVisibility(false)}
        />
        <PurchaseConfirmationModalBid
          bidCalculation={isBidConfirmationVisible?.bidCalculation}
          bidValue={isBidConfirmationVisible?.bidValue || false}
          isAwaitingResponse={isAwaitingBidResponse}
          isOptedOut={isBidConfirmationOptOut}
          listingUuid={listingUuid}
          onBidConfirmOptOut={() => setBidConfirmationOptOut(!isBidConfirmationOptOut)}
          onClose={() => { setBidConfirmationOptOut(false); setBidConfirmationVisibility(false) }}
          onSubmit={handleBidConfirm}
        />
        <BidHistoryModal
          isBidHistoryVisible={isBidHistoryVisible}
          listingUuid={listingUuid}
          setBidHistoryVisibility={setBidHistoryVisibility}
        />
    </div>
  )
}

ListingSale.propTypes = {
  currentLanguageCode: PropTypes.string, // eslint-disable-line react/no-unused-prop-types -- memo prop from parent; not read in body
  hasStarted: PropTypes.bool,
  listingUuid: PropTypes.string.isRequired
}

export default React.memo(ListingSale)
